import { formatDateTime } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import {
  MedicationDeliveryPreference,
  Participant,
} from '@seen/core/models/patients/participant'
import { buttonVariants, cn } from '@seen/ui'
import type { TablerIcon } from '@tabler/icons-react'
import {
  IconBuilding,
  IconCheck,
  IconHome,
  IconPencil,
  IconTruckDelivery,
} from '@tabler/icons-react'
import { parseISO } from 'date-fns'
import { type JSX, useMemo, useState } from 'react'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#src/components/core/index.ts'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'

const PREFERENCE_OPTIONS = [
  {
    value: MedicationDeliveryPreference.Center,
    label: 'Deliver to center',
  },
  {
    value: MedicationDeliveryPreference.Home,
    label: 'Deliver to home',
  },
  {
    value: null,
    label: '---',
  },
] as const

const PREFERENCE_ICONS: Record<MedicationDeliveryPreference, TablerIcon> = {
  [MedicationDeliveryPreference.Center]: IconBuilding,
  [MedicationDeliveryPreference.Home]: IconHome,
}

export interface MedicationDeliveryPreferenceFieldProps {
  participant: Participant
  className?: string
  size?: 'card' | 'page'
}

export function MedicationDeliveryPreferenceField({
  participant,
  className,
  size = 'page',
}: MedicationDeliveryPreferenceFieldProps): JSX.Element | null {
  const enabled = useFeatureFlag(featureFlags.medicationDeliveryPreference)

  if (!enabled) {
    return null
  }

  return (
    <EnabledMedicationDeliveryPreferenceField
      participant={participant}
      className={className}
      size={size}
    />
  )
}

function EnabledMedicationDeliveryPreferenceField({
  participant,
  className,
  size,
}: Required<
  Pick<MedicationDeliveryPreferenceFieldProps, 'participant' | 'size'>
> &
  Pick<MedicationDeliveryPreferenceFieldProps, 'className'>): JSX.Element {
  const storedEditedAt = participant.medicationDeliveryPreferenceUpdatedAt
  const currentPreference = participant.medicationDeliveryPreference
  const { data: versions, hasNextPage } = useResourceVersions(
    Participant,
    participant.id,
    { query: { enabled: !storedEditedAt && Boolean(currentPreference) } }
  )
  const editedAt =
    storedEditedAt ??
    (currentPreference && versions
      ? preferenceSetAtFromHistory(versions, currentPreference, hasNextPage)
      : undefined)

  return (
    <ResourceFieldForm
      instance={participant}
      valueFn={(p) => p.medicationDeliveryPreference}
      patchFn={(p, value) => {
        p.medicationDeliveryPreference = value
      }}
      className={className}
      render={({ field: { value, onChange, disabled } }) => (
        <PreferenceControl
          value={value}
          onChange={onChange}
          disabled={disabled}
          size={size}
          editedAt={editedAt}
        />
      )}
    />
  )
}

// Preferences saved before the updated-at extension existed carry no
// timestamp, so the change time is the oldest version in the newest unbroken
// run of the current value. Without an earlier differing version on a loaded
// page, the run may continue on unloaded pages and the date is unknown.
export function preferenceSetAtFromHistory(
  versionsNewestFirst: readonly Participant[],
  preference: MedicationDeliveryPreference,
  hasOlderVersions: boolean
): string | undefined {
  let setAt: string | undefined
  for (const version of versionsNewestFirst) {
    if (version.medicationDeliveryPreference !== preference) {
      return setAt
    }
    setAt = version.lastUpdated
  }
  return hasOlderVersions ? undefined : setAt
}

function deliveryPreferenceEditedLabel(
  editedAt: string | undefined
): string | undefined {
  if (!editedAt) {
    return undefined
  }
  const edited = parseISO(editedAt)
  if (Number.isNaN(edited.getTime())) {
    return undefined
  }
  return `Last edited ${formatDateTime(edited)}`
}

function PreferenceControl({
  value,
  onChange,
  disabled,
  size,
  editedAt,
}: {
  value: MedicationDeliveryPreference | null
  onChange: (value: MedicationDeliveryPreference | null) => void
  disabled?: boolean
  size: 'card' | 'page'
  editedAt?: string
}): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false)
  const [search, setSearch] = useState('')
  const selectedOption = PREFERENCE_OPTIONS.find(
    (option) => option.value === value
  )
  const label = value
    ? (selectedOption?.label ?? 'Add delivery preference')
    : 'Add delivery preference'
  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) {
      return PREFERENCE_OPTIONS
    }
    return PREFERENCE_OPTIONS.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearch)
    )
  }, [search])

  const PreferenceIcon = value ? PREFERENCE_ICONS[value] : IconTruckDelivery

  const content = (
    <>
      <PreferenceIcon
        className="size-4 shrink-0 text-stone-700 dark:text-stone-300"
        stroke={1.5}
      />
      <span className="flex-1 text-left">{label}</span>
      {value && !disabled && (
        <IconPencil className="size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
      )}
    </>
  )
  const triggerClassName =
    size === 'card'
      ? cn(
          // The dark border is zinc-500 so the field's edge clears 3:1
          // against the dark card; the theme border token is 1.3:1 there.
          'group flex h-7 w-full items-center gap-1 rounded-md border bg-white px-2.5 text-xs leading-4 shadow-xs dark:border-zinc-500 dark:bg-card',
          !disabled && 'hover:bg-stone-50 dark:hover:bg-accent',
          !value && 'text-muted-foreground'
        )
      : cn(
          buttonVariants({ variant: 'outline' }),
          'group',
          !value && 'text-muted-foreground'
        )

  const editedLabel = deliveryPreferenceEditedLabel(editedAt)
  const editedTooltip = editedLabel ? (
    <TooltipContent
      side="bottom"
      align={size === 'card' ? 'start' : 'end'}
      className={size === 'card' ? 'px-2.5 py-1 text-xs' : undefined}
    >
      {editedLabel}
    </TooltipContent>
  ) : null

  if (disabled) {
    const trigger = (
      <div className={cn(triggerClassName, 'opacity-50')}>{content}</div>
    )
    if (!editedTooltip) {
      return trigger
    }
    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        {editedTooltip}
      </Tooltip>
    )
  }

  const triggerButton = (
    <button type="button" className={triggerClassName}>
      {content}
    </button>
  )
  const menu = (
    <DropdownMenu
      onOpenChange={(open) => {
        setMenuOpen(open)
        if (!open) {
          setSearch('')
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        {editedTooltip ? (
          <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
        ) : (
          triggerButton
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-(--radix-dropdown-menu-trigger-width) min-w-44 p-1"
      >
        <div className="border-b px-2 py-1">
          <input
            aria-label="Filter medication delivery preferences"
            className="h-7 w-full bg-transparent text-xs outline-hidden placeholder:text-muted-foreground"
            placeholder="Filter..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
            }}
            onKeyDown={(event) => {
              event.stopPropagation()
            }}
          />
        </div>
        {filteredOptions.map((option) => (
          <DropdownMenuItem
            key={option.label}
            className="gap-2 text-xs"
            onSelect={() => {
              onChange(option.value)
            }}
          >
            <span className="flex size-4 items-center justify-center">
              {option.value === value && <IconCheck className="size-4" />}
            </span>
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  if (!editedTooltip) {
    return menu
  }

  return (
    <Tooltip>
      {menu}
      {!menuOpen && editedTooltip}
    </Tooltip>
  )
}
